/**
 * CL-STORAGE-001F — Explicit Storage authorization domain + audience.
 *
 * These fields are Admin-controlled and stored on pmProjectFiles. Storage
 * rules never infer them from filename, category, role, persona, company,
 * trade stage, full_project, or collaboratorEmails.
 *
 * Missing storageAuthDomain on an already-versioned file is drawing-legacy
 * so existing Hooke/Lawson/C101 versioned drawings do not regress. New
 * photos/library/client-document versioned objects MUST set both fields
 * before publication.
 */

export const STORAGE_AUTH_DOMAINS = Object.freeze({
  DRAWING: "drawing",
  PROJECT_PHOTO: "project_photo",
  CLIENT_DOCUMENT: "client_document",
  CONTROLLED_DOCUMENT: "controlled_document",
  HISTORICAL_ARCHIVE: "historical_archive",
  ADMIN_ONLY: "admin_only",
});

export const STORAGE_AUDIENCES = Object.freeze({
  ADMIN_ONLY: "admin_only",
  SELECTED_USERS: "selected_users",
  PROJECT_CLIENT: "project_client",
  PROJECT_MEMBERS: "project_members",
});

export const STORAGE_AUTH_DOMAIN_VALUES = Object.freeze(Object.values(STORAGE_AUTH_DOMAINS));
export const STORAGE_AUDIENCE_VALUES = Object.freeze(Object.values(STORAGE_AUDIENCES));

export const STORAGE_AUTH_FIELD_KEYS = Object.freeze([
  "storageAuthDomain",
  "storageAudience",
  "storagePublicationState",
  "storageSchemaVersion",
  "storageReady",
  "storageOriginalVerified",
  "storagePath",
  "thumbnailStoragePath",
  "path",
  "status",
  "deleted",
  "storageViewAuthUids",
  "storageAdminAuthUids",
]);

export const STORAGE_AUTH_LOOKUP_BUDGET = Object.freeze({
  adminReadUniqueDocs: 1,
  collaboratorReadUniqueDocs: 2,
  documents: Object.freeze(["pmProjectFiles/{fileId}", "projectAssignments/{projectId}__{uid}"]),
  neverQueried: Object.freeze(["projectFileShares", "users", "pmProjects", "invites"]),
  originalAndThumbnailSameExpression: true,
});

function safeStr(value) {
  return String(value ?? "").trim();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

export function normalizeStorageAuthDomain(raw) {
  const key = lower(raw);
  return STORAGE_AUTH_DOMAIN_VALUES.includes(key) ? key : "";
}

export function normalizeStorageAudience(raw) {
  const key = lower(raw);
  return STORAGE_AUDIENCE_VALUES.includes(key) ? key : "";
}

export function isRecognizedStorageAuthDomain(raw) {
  return Boolean(normalizeStorageAuthDomain(raw));
}

export function isRecognizedStorageAudience(raw) {
  return Boolean(normalizeStorageAudience(raw));
}

/**
 * Empty domain is drawing-legacy (pre-001F versioned drawings).
 * Explicit unknown is not recognized.
 */
export function isDrawingStorageAuthDomain(file = {}) {
  const domain = normalizeStorageAuthDomain(file?.storageAuthDomain);
  return !safeStr(file?.storageAuthDomain) || domain === STORAGE_AUTH_DOMAINS.DRAWING;
}

export function drawingStorageAudienceOf(file = {}) {
  const explicit = normalizeStorageAudience(file?.storageAudience);
  if (!explicit) return STORAGE_AUDIENCES.SELECTED_USERS;
  return explicit;
}

export function requiredAssignmentCapability(domain) {
  const d = normalizeStorageAuthDomain(domain);
  if (d === STORAGE_AUTH_DOMAINS.DRAWING || !d) return "canReviewDrawings";
  if (d === STORAGE_AUTH_DOMAINS.PROJECT_PHOTO) return "canViewPhotos";
  return "";
}

export function storageAudienceIsSupportedForDomain(domain, audience) {
  const d = normalizeStorageAuthDomain(domain) || STORAGE_AUTH_DOMAINS.DRAWING;
  const a = normalizeStorageAudience(audience);
  if (!a) return d === STORAGE_AUTH_DOMAINS.DRAWING;
  if (a === STORAGE_AUDIENCES.ADMIN_ONLY) return true;
  if (d === STORAGE_AUTH_DOMAINS.DRAWING) return a === STORAGE_AUDIENCES.SELECTED_USERS;
  if (d === STORAGE_AUTH_DOMAINS.PROJECT_PHOTO) {
    return (
      a === STORAGE_AUDIENCES.SELECTED_USERS ||
      a === STORAGE_AUDIENCES.PROJECT_CLIENT ||
      a === STORAGE_AUDIENCES.PROJECT_MEMBERS
    );
  }
  if (d === STORAGE_AUTH_DOMAINS.CLIENT_DOCUMENT) {
    return a === STORAGE_AUDIENCES.SELECTED_USERS || a === STORAGE_AUDIENCES.PROJECT_CLIENT;
  }
  if (d === STORAGE_AUTH_DOMAINS.CONTROLLED_DOCUMENT) {
    return a === STORAGE_AUDIENCES.SELECTED_USERS;
  }
  if (d === STORAGE_AUTH_DOMAINS.HISTORICAL_ARCHIVE || d === STORAGE_AUTH_DOMAINS.ADMIN_ONLY) {
    return a === STORAGE_AUDIENCES.ADMIN_ONLY;
  }
  return false;
}

export function mapPortalAudienceToStorageAudience(portalAudience) {
  const key = lower(portalAudience);
  if (key === "admin_only") return STORAGE_AUDIENCES.ADMIN_ONLY;
  if (key === "selected_collaborators" || key === "selected_users") return STORAGE_AUDIENCES.SELECTED_USERS;
  if (key === "client_only" || key === "project_client") return STORAGE_AUDIENCES.PROJECT_CLIENT;
  if (key === "project_members") return STORAGE_AUDIENCES.PROJECT_MEMBERS;
  return "";
}

export function mapPortalDomainToStorageAuthDomain(portalDomain) {
  const key = lower(portalDomain);
  if (key === "drawing" || key === "drawing_review") return STORAGE_AUTH_DOMAINS.DRAWING;
  if (key === "project_photo") return STORAGE_AUTH_DOMAINS.PROJECT_PHOTO;
  if (key === "client_document" || key === "client_supporting_document" || key === "requested_document") {
    return STORAGE_AUTH_DOMAINS.CLIENT_DOCUMENT;
  }
  if (key === "controlled_document" || key === "project_library") return STORAGE_AUTH_DOMAINS.CONTROLLED_DOCUMENT;
  if (key === "historical_archive") return STORAGE_AUTH_DOMAINS.HISTORICAL_ARCHIVE;
  if (key === "admin_only") return STORAGE_AUTH_DOMAINS.ADMIN_ONLY;
  return "";
}

export function classifyVersionedUploadSurface({
  libraryType = "",
  category = "",
  projectFileDomain = "",
  uploadContext = "",
} = {}) {
  const lt = lower(libraryType);
  const cat = lower(category);
  const domain = lower(projectFileDomain);
  const ctx = lower(uploadContext);
  if (lt === "drawing" || cat === "drawings" || domain === "drawing" || domain === "drawing_review" || ctx === "drawing" || ctx === "drawings") {
    return {
      versioned: true,
      storageAuthDomain: STORAGE_AUTH_DOMAINS.DRAWING,
      defaultAudience: STORAGE_AUDIENCES.SELECTED_USERS,
      blocked: false,
      reason: "",
    };
  }
  if (lt === "photo" || cat === "photos" || domain === "project_photo" || ctx === "photo" || ctx === "photos") {
    return {
      versioned: false,
      storageAuthDomain: STORAGE_AUTH_DOMAINS.PROJECT_PHOTO,
      defaultAudience: "",
      blocked: true,
      reason: "photo_upload_stays_on_existing_path_until_explicit_audience",
    };
  }
  if (lt === "client_document" || domain === "client_document" || ctx === "client_document") {
    return {
      versioned: false,
      storageAuthDomain: STORAGE_AUTH_DOMAINS.CLIENT_DOCUMENT,
      defaultAudience: STORAGE_AUDIENCES.PROJECT_CLIENT,
      blocked: true,
      reason: "client_document_upload_stays_on_clientUploads_prefix",
    };
  }
  if (lt === "controlled_document" || domain === "controlled_document" || ctx === "library" || ctx === "controlled_document") {
    return {
      versioned: false,
      storageAuthDomain: STORAGE_AUTH_DOMAINS.CONTROLLED_DOCUMENT,
      defaultAudience: "",
      blocked: true,
      reason: "library_upload_blocked_until_explicit_saved_audience",
    };
  }
  return {
    versioned: false,
    storageAuthDomain: "",
    defaultAudience: "",
    blocked: true,
    reason: "unknown_domain_not_published_on_versioned_prefix",
  };
}

export function buildVersionedStorageAuthorizationFields({
  storageAuthDomain = STORAGE_AUTH_DOMAINS.DRAWING,
  storageAudience = STORAGE_AUDIENCES.SELECTED_USERS,
} = {}) {
  const domain = normalizeStorageAuthDomain(storageAuthDomain);
  const audience = normalizeStorageAudience(storageAudience);
  if (!domain || !audience || !storageAudienceIsSupportedForDomain(domain, audience)) {
    return null;
  }
  return { storageAuthDomain: domain, storageAudience: audience };
}

export function evaluateVersionedStorageRead({
  signedIn = false,
  authUid = "",
  file = null,
  assignment = null,
} = {}) {
  const uid = safeStr(authUid);
  if (!signedIn || !file || typeof file !== "object") {
    return { allow: false, reason: "unauthenticated_or_missing_file", lookups: 0 };
  }
  if (safeStr(file.projectId) && assignment && safeStr(assignment.projectId) && safeStr(file.projectId) !== safeStr(assignment.projectId)) {
    return { allow: false, reason: "project_mismatch", lookups: 2 };
  }
  const adminUids = Array.isArray(file.storageAdminAuthUids) ? file.storageAdminAuthUids : [];
  if (uid && adminUids.includes(uid)) {
    return { allow: true, reason: "storage_admin", lookups: 1 };
  }
  if (!assignment || lower(assignment.status || "active") !== "active" || safeStr(assignment.userId) !== uid) {
    return { allow: false, reason: "no_active_assignment", lookups: 2 };
  }
  if (file.deleted === true || lower(file.status || "active") === "deleted") {
    return { allow: false, reason: "deleted", lookups: 2 };
  }
  const schema = Number(file.storageSchemaVersion || 0);
  if (schema >= 2 && lower(file.storagePublicationState) !== "ready") {
    return { allow: false, reason: "not_ready", lookups: 2 };
  }
  const domainRaw = safeStr(file.storageAuthDomain);
  const domain = normalizeStorageAuthDomain(domainRaw);
  if (domainRaw && !domain) return { allow: false, reason: "unknown_domain", lookups: 2 };
  const audienceRaw = safeStr(file.storageAudience);
  const audience = normalizeStorageAudience(audienceRaw);
  if (audienceRaw && !audience) return { allow: false, reason: "unknown_audience", lookups: 2 };
  if (!storageAudienceIsSupportedForDomain(domain || STORAGE_AUTH_DOMAINS.DRAWING, audience || (isDrawingStorageAuthDomain(file) ? STORAGE_AUDIENCES.SELECTED_USERS : ""))) {
    return { allow: false, reason: "unsupported_audience", lookups: 2 };
  }
  const perms = assignment.permissions && typeof assignment.permissions === "object" ? assignment.permissions : {};
  const viewUids = Array.isArray(file.storageViewAuthUids) ? file.storageViewAuthUids : [];
  const effectiveDomain = isDrawingStorageAuthDomain(file) ? STORAGE_AUTH_DOMAINS.DRAWING : domain;
  const effectiveAudience = audience || (effectiveDomain === STORAGE_AUTH_DOMAINS.DRAWING ? STORAGE_AUDIENCES.SELECTED_USERS : "");

  if (effectiveAudience === STORAGE_AUDIENCES.ADMIN_ONLY) {
    return { allow: false, reason: "admin_only_audience", lookups: 2 };
  }
  if (effectiveDomain === STORAGE_AUTH_DOMAINS.HISTORICAL_ARCHIVE || effectiveDomain === STORAGE_AUTH_DOMAINS.ADMIN_ONLY) {
    return { allow: false, reason: "unsupported_domain", lookups: 2 };
  }
  if (effectiveDomain === STORAGE_AUTH_DOMAINS.DRAWING) {
    if (perms.canReviewDrawings !== true) return { allow: false, reason: "missing_canReviewDrawings", lookups: 2 };
    if (effectiveAudience !== STORAGE_AUDIENCES.SELECTED_USERS) return { allow: false, reason: "drawing_requires_selected_users", lookups: 2 };
    if (!viewUids.includes(uid)) return { allow: false, reason: "not_in_view_acl", lookups: 2 };
    return { allow: true, reason: "drawing_selected_user", lookups: 2 };
  }
  if (effectiveDomain === STORAGE_AUTH_DOMAINS.PROJECT_PHOTO) {
    if (perms.canViewPhotos !== true) return { allow: false, reason: "missing_canViewPhotos", lookups: 2 };
    if (effectiveAudience === STORAGE_AUDIENCES.SELECTED_USERS && viewUids.includes(uid)) {
      return { allow: true, reason: "photo_selected_user", lookups: 2 };
    }
    if (effectiveAudience === STORAGE_AUDIENCES.PROJECT_CLIENT && lower(assignment.role) === "client") {
      return { allow: true, reason: "photo_project_client", lookups: 2 };
    }
    if (effectiveAudience === STORAGE_AUDIENCES.PROJECT_MEMBERS) {
      return { allow: true, reason: "photo_project_members_with_canViewPhotos", lookups: 2 };
    }
    return { allow: false, reason: "photo_audience_denied", lookups: 2 };
  }
  if (effectiveDomain === STORAGE_AUTH_DOMAINS.CLIENT_DOCUMENT) {
    if (effectiveAudience === STORAGE_AUDIENCES.SELECTED_USERS && viewUids.includes(uid)) {
      return { allow: true, reason: "client_document_selected_user", lookups: 2 };
    }
    if (effectiveAudience === STORAGE_AUDIENCES.PROJECT_CLIENT && lower(assignment.role) === "client") {
      return { allow: true, reason: "client_document_project_client", lookups: 2 };
    }
    return { allow: false, reason: "client_document_denied", lookups: 2 };
  }
  if (effectiveDomain === STORAGE_AUTH_DOMAINS.CONTROLLED_DOCUMENT) {
    if (effectiveAudience === STORAGE_AUDIENCES.SELECTED_USERS && viewUids.includes(uid)) {
      return { allow: true, reason: "controlled_document_selected_user", lookups: 2 };
    }
    return { allow: false, reason: "controlled_document_denied", lookups: 2 };
  }
  return { allow: false, reason: "deny", lookups: 2 };
}

export function proposeLawsonMigrationClass({
  leftoverCatchAllDependent = false,
  pathKind = "",
  inventoryDomain = "",
  explicitAudience = "",
  lifecycle = "active",
} = {}) {
  if (lifecycle !== "active") return "orphan_not_a_main_record";
  if (pathKind === "client_uploads" || pathKind === "collaborator_revisions") return "keep_dedicated_prefix";
  if (!leftoverCatchAllDependent) return pathKind === "versioned_original" ? "already_versioned" : "orphan_not_a_main_record";
  if (inventoryDomain === "project_photo") {
    return explicitAudience ? "ready_for_photo_pilot" : "blocked_ambiguous_audience";
  }
  if (inventoryDomain === "project_library" || inventoryDomain === "controlled_document") {
    return explicitAudience ? "ready_for_controlled_document_pilot" : "blocked_ambiguous_audience";
  }
  if (inventoryDomain === "unknown_malformed") return "blocked_unknown_domain";
  if (inventoryDomain === "historical_archive") return "blocked_unknown_domain";
  if (inventoryDomain === "client_document" || inventoryDomain === "client_upload") {
    return pathKind === "client_uploads" ? "keep_dedicated_prefix" : "blocked_ambiguous_audience";
  }
  return "blocked_unknown_domain";
}
